import React, { useState, useRef } from 'react';
import SettingsModal from './SettingsModal.jsx';
import TaskDescriptionModal from './TaskDescriptionModal.jsx';
import RoleModal from './RoleModal.jsx';
import {
  Save, FolderOpen, Download, Upload, RefreshCw,
  Settings, ChevronDown, User, FileText,
} from 'lucide-react';

const Toolbar = ({
  onSave,
  onLoad,
  onReload,
  onDownload,
  onUpload,
  onNewGraph,
  autoClassify,
  setAutoClassify,
  autoSentiment,
  setAutoSentiment,
  taskDescription,
  setTaskDescription,
  roleLabel,
  onRoleChange,
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const uploadRef = useRef(null);

  const handleSave = () => {
    const graphData = onSave();
    if (graphData) {
      localStorage.setItem('graphData', JSON.stringify(graphData, null, 2));
    }
  };

  const handleLoad = () => {
    const graphData = localStorage.getItem('graphData');
    try {
      if (graphData) onLoad(JSON.parse(graphData));
    } catch {}
  };

  const handleDownloadFile = () => {
    const graphData = onSave();
    if (!graphData) return;
    const blob = new Blob([JSON.stringify(graphData)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'graph.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data && typeof onUpload === 'function') onUpload(data);
      } catch {}
    };
    reader.readAsText(file);
  };

  return (
    <div className="toolbar">
      {/* ── Graph actions ── */}
      <div className="toolbar-group">
        <button className="toolbar-btn" onClick={onNewGraph} title="New graph">
          <FileText size={16} />
        </button>
        <button className="toolbar-btn" onClick={handleSave} title="Save">
          <Save size={16} />
        </button>
        <button className="toolbar-btn" onClick={handleLoad} title="Load">
          <FolderOpen size={16} />
        </button>
        <button className="toolbar-btn" onClick={handleDownloadFile} title="Download JSON">
          <Download size={16} />
        </button>
        <button className="toolbar-btn" onClick={() => uploadRef.current?.click()} title="Upload JSON">
          <Upload size={16} />
        </button>
        <input
          ref={uploadRef}
          type="file"
          accept="application/json"
          onChange={handleUploadFile}
          style={{ display: 'none' }}
        />
        <button className="toolbar-btn" onClick={onReload} title="Reload">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="toolbar-separator" />

      {/* ── Role pill ── */}
      <button
        className={`role-pill${roleLabel ? ' role-pill--set' : ''}`}
        onClick={() => setShowRoleModal(true)}
        title="Set exploration role"
      >
        <User size={13} />
        <span>{roleLabel || 'Set role'}</span>
        <ChevronDown size={12} />
      </button>

      {/* ── Task description (if set) ── */}
      {taskDescription && (
        <button
          className="task-pill"
          onClick={() => setShowTaskModal(true)}
          title={taskDescription}
        >
          <span>{taskDescription.slice(0, 40)}{taskDescription.length > 40 ? '…' : ''}</span>
        </button>
      )}
      {!taskDescription && (
        <button
          className="toolbar-btn toolbar-btn--text"
          onClick={() => setShowTaskModal(true)}
          title="Set task description"
        >
          Task
        </button>
      )}

      {/* ── Right-side controls ── */}
      <div className="toolbar-spacer" />
      <button className="toolbar-btn" onClick={() => setShowSettings(true)} title="Settings">
        <Settings size={16} />
      </button>

      {/* ── Modals ── */}
      <SettingsModal
        show={showSettings}
        onClose={() => setShowSettings(false)}
        autoClassify={autoClassify}
        setAutoClassify={setAutoClassify}
        autoSentiment={autoSentiment}
        setAutoSentiment={setAutoSentiment}
      />
      <TaskDescriptionModal
        show={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        description={taskDescription}
        setDescription={setTaskDescription}
      />
      <RoleModal
        show={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        currentRole={roleLabel}
        onRoleUpdate={onRoleChange}
      />
    </div>
  );
};

export default Toolbar;
